import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FastField, FieldProps, Form, Formik, FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import * as yup from 'yup';

import { processIngredients } from 'Utils/helpers/processMenuItem';
import { generateIngredientsQuery } from 'Utils/helpers/queryGenerators';
import FormikAutoSave from 'components/FormikAutoSave';
import IngredientsSearchField from 'components/IngredientsSearchField';
import { GoldiTextField } from 'components/InputField';
import { Ingredient } from 'interfaces/IngredientInterface';
import debounce from 'lodash/debounce';
import { useDietaryPreferences } from 'providers/DietaryPreferenceProvider';

type IngredientDialogPropsType = {
  isOpen: boolean;
  handleCloseDialog: () => void;
  handleSave: (ingredient: Ingredient) => void;
  ingredient: Ingredient;
  saveDisabled?: boolean;
  isEdit?: boolean;
};

const DEBOUNCE_DELAY_MS = 500;

const IngredientDialog = ({
  isOpen,
  saveDisabled = false,
  ingredient,
  handleCloseDialog,
  handleSave,
  isEdit = false,
}: IngredientDialogPropsType) => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const existingIngredients: Ingredient[] =
    queryClient.getQueryData(generateIngredientsQuery()) || [];
  const { dietKeyToName: Diets, allergyKeyToName: Allergies } =
    useDietaryPreferences();

  useEffect(() => {
    setModalIsOpen(isOpen);
  }, [isOpen]);

  // Slightly hacky, we need to use this state to stop the auto-save from firing when we close the dialog
  useEffect(() => {
    if (!modalIsOpen) {
      handleCloseDialog();
    }
  }, [modalIsOpen]); //Do not add handleCloseDialog to the dependency array, it will stop modal from re-opening

  // debounce validation code from https://stackoverflow.com/questions/58576825/yup-formik-async-validation-with-debounce=
  const validationFunction = async (
    value: string | undefined,
    resolve: any
  ) => {
    // Don't need to perform this validation if we're editing existing ingredient
    if (isEdit) resolve(true);

    try {
      const existingIngredient = existingIngredients.find(
        (i: Ingredient) => i.id === value
      );
      if (existingIngredient) {
        resolve(false);
      } else {
        resolve(true);
      }
    } catch (error) {
      resolve(false);
    }
  };

  const validationDebounced = debounce(validationFunction, DEBOUNCE_DELAY_MS);

  const ingredientValidationSchema = yup.object({
    id: yup
      .string()
      .required()
      .test(
        'is-valid-id',
        'ID Already Exists',
        (value) => new Promise((resolve) => validationDebounced(value, resolve))
      ),
    title: yup.string().required(),
  });

  return (
    <Dialog
      // fullScreen
      open={isOpen}
      onClose={(_e, reason) => {
        // disable closing dialog when clicking outside of dialog
        if (reason && reason == 'backdropClick') return;
        setModalIsOpen(false);
      }}
      aria-labelledby='new-menu-item-dialog-title'
      aria-describedby='new-menu-item-dialog-description'
      maxWidth='sm'
      fullWidth={true}
      disableEscapeKeyDown
    >
      <Formik
        enableReinitialize={true}
        initialValues={ingredient}
        onSubmit={(ingredient: Ingredient) => {
          handleSave(ingredient);
        }}
        validationSchema={ingredientValidationSchema}
        validateOnMount={true}
        initialErrors={{ id: 'Required', title: 'Required' }}
      >
        {(formik: FormikProps<Ingredient>) => {
          const {
            values,
            handleBlur,
            handleChange,
            isValid,
            handleSubmit,
            setFieldValue,
          } = formik;

          return (
            <Form>
              <DialogTitle>
                <Box display={'flex'} alignItems={'center'} width={'100%'}>
                  <Typography width={'100%'} fontSize={'1.25rem'}>
                    {isEdit
                      ? `Edit ${
                          ingredient.title.length
                            ? ingredient.title
                            : 'Ingredient'
                        }`
                      : 'New Ingredient'}
                  </Typography>
                  <Box display={'flex'} justifyContent={'flex-end'}>
                    <IconButton
                      onClick={handleCloseDialog}
                      disableRipple
                      sx={{ cursor: 'pointer', float: 'right', width: '20px' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
              </DialogTitle>
              <Divider />
              <DialogContent>
                <Box>
                  <Grid container spacing={1}>
                    {/* Item ID */}
                    <Grid item xs={9}>
                      <Typography>ID / NDB Number</Typography>
                      <FastField
                        name='id'
                        type='text'
                        render={({ field, form: { errors } }: FieldProps) => (
                          <GoldiTextField
                            {...field}
                            id={field.name}
                            value={values.id}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors[field.name]}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <FormControlLabel
                        value='top'
                        control={
                          <Switch
                            checked={values.active}
                            onChange={handleChange}
                            name={'active'}
                          />
                        }
                        label='Active'
                        labelPlacement='top'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>Name</Typography>
                      <FastField
                        name='title'
                        type='text'
                        render={({ field, form: { errors } }: FieldProps) => (
                          <GoldiTextField
                            {...field}
                            id={field.name}
                            value={values.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors[field.name]}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {/* Item Description */}
                      <Box width={'100%'}>
                        <Typography>Description</Typography>
                        <FastField
                          name='description'
                          type='text'
                          render={({ field, form: { errors } }: FieldProps) => (
                            <GoldiTextField
                              {...field}
                              id={field.name}
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={!!errors[field.name]}
                            />
                          )}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>Ingredients</Typography>
                      <FastField
                        name={'ingredients'}
                        key={'ingredients'}
                        type='text'
                        render={({ field, form: { errors } }: FieldProps) => (
                          <IngredientsSearchField
                            ingredients={values.ingredients || []}
                            handleSetIngredients={(
                              ingredients: Ingredient[]
                            ) => {
                              const { allergies, diets } =
                                processIngredients(ingredients);
                              setFieldValue('ingredients', ingredients);
                              setFieldValue('allergies', allergies);
                              setFieldValue('diets', diets);
                            }}
                            rawIngredientsOnly={true}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {/* Item Allergies */}
                      <Box width={'100%'}>
                        <Typography>Dislikes</Typography>
                        <FastField name='allergies' type='text'>
                          {() => (
                            <Autocomplete
                              multiple
                              limitTags={3}
                              onChange={(e, value) => {
                                setFieldValue(`allergies`, value);
                              }}
                              options={Allergies ? Object.keys(Allergies) : []}
                              getOptionLabel={(option: string) =>
                                Allergies?.[option] || option
                              }
                              value={values.allergies || []}
                              renderInput={(params) => (
                                <TextField {...params} placeholder='Dislikes' />
                              )}
                            />
                          )}
                        </FastField>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      {/* Item Diets */}
                      <Box width={'100%'}>
                        <Typography>Preferences</Typography>
                        <FastField name='diets' type='text'>
                          {() => (
                            <Autocomplete
                              multiple
                              limitTags={2}
                              onChange={(e, value) => {
                                setFieldValue(`diets`, value);
                              }}
                              options={Diets ? Object.keys(Diets) : []}
                              getOptionLabel={(option: string) =>
                                Diets?.[option] || option
                              }
                              value={values.diets || []}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder='Preferences'
                                />
                              )}
                            />
                          )}
                        </FastField>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <FormikAutoSave
                  canAutoSave={isValid && modalIsOpen}
                  sx={{
                    mr: 2,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                />
                <Button
                  sx={{ mr: 2 }}
                  onClick={() => {
                    setModalIsOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  disableElevation
                  onClick={() => {
                    handleSubmit();
                    setModalIsOpen(false);
                  }}
                  disabled={!isValid || saveDisabled}
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default IngredientDialog;
